import { ThumbnailType, ImageFormat, cdnService, ImageFit, scroll, waitForImage, FeatureEnabledV2, SessionMixin } from '@gig/magic-common';
import { __extends, __awaiter, __generator, __decorate, __metadata } from 'tslib';
import { Prop, Watch, Emit, Component, Mixins } from 'vue-property-decorator';
import { GAMES_LIST, USER_JURISDICTION_UPDATED, Feature } from '@gig/magic-messages';
import { CtaStyle } from '@gig/magic-sdk/dist/util/interfaces';
import '../../mixins/static-page.mixin.js';
import { PromotionsMixin } from '../../mixins/promotions.mixin.js';
import '../../mixins/landing-page.mixin.js';
import { CompetitionsMixin } from '../../mixins/competitions.mixin.js';
import { isUKGC, isSGA } from '@gig/magic-jurisdictions';
import { PromotionsOptinMixin } from '../../mixins/promotions-optin.mixin.js';

/**
 * Component displaying full page promotion
 */
var PromotionComponent = /** @class */ (function (_super) {
    __extends(PromotionComponent, _super);
    function PromotionComponent() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.isOptingIn = false;
        _this.gamesListSubscription = null;
        /**
         * Whether the promotion games feature is enabled
         * If enabled it will show game thumbnails inside promotion
         */
        _this.promotionGamesEnabled = false;
        /**
         * Thumbnail options for the promotion games
         */
        _this.thumbnailType = ThumbnailType.PROMOTION_GAMES;
        _this.isUKGCJurisdiction = false;
        _this.isSGAJurisdiction = false;
        _this.userJurisdictionSubscription = null;
        return _this;
    }
    PromotionComponent.prototype.created = function () {
        var _this = this;
        this.gamesListSubscription = window.eventBus.subscribe(GAMES_LIST, function (e) {
            _this.games = e.payload;
        }, { replay: true });
        this.userJurisdictionSubscription =
            window.eventBus.subscribe(USER_JURISDICTION_UPDATED, function (event) {
                _this.isUKGCJurisdiction = isUKGC(event.payload);
                _this.isSGAJurisdiction = isSGA(event.payload);
            }, { replay: true });
    };
    PromotionComponent.prototype.destroyed = function () {
        var _a, _b;
        (_a = this.gamesListSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        (_b = this.userJurisdictionSubscription) === null || _b === void 0 ? void 0 : _b.unsubscribe();
    };
    Object.defineProperty(PromotionComponent.prototype, "promotion", {
        get: function () {
            var _this = this;
            if (!this.promotions) {
                return null;
            }
            return this.promotions.find(function (_a) {
                var slug = _a.slug;
                return slug === _this.slug;
            }) || null;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PromotionComponent.prototype, "isOptInPromo", {
        //Checks whether the promotion is the Opt-in type and the user has opted-in or not
        get: function () {
            var _this = this;
            if (!this.promotion) {
                return false;
            }
            var promotionsArray = this.promotion.values.cta.filter(function (cta) {
                return _this.isOptIn(cta);
            });
            return promotionsArray.length === 0 || this.isOptedIn;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PromotionComponent.prototype, "isOptedIn", {
        //Checks whether the user has opted-in in the current promotion
        get: function () {
            var _this = this;
            if (!this.promotion || !this.isLoggedIn) {
                return false;
            }
            var optInCtas = this.promotion.values.cta.filter(this.isOptIn);
            return optInCtas.some(function (cta) {
                return Array.isArray(cta.roles) &&
                    cta.roles.some(function (role) { return _this.userRoles.includes(role); });
            });
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PromotionComponent.prototype, "background", {
        get: function () {
            if (!this.promotion || !this.promotion.background) {
                return null;
            }
            // always get background in original size
            var imageParams = {
                width: 1.0,
                height: 1.0,
                format: ImageFormat.WEBP,
            };
            if (this.isMobile && this.promotion.mobileBackground) {
                return cdnService.getPromotionMobileBackgroundUrl(this.promotion.mobileBackground, imageParams);
            }
            return cdnService.getPromotionBackgroundUrl(this.promotion.background, imageParams);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PromotionComponent.prototype, "src", {
        get: function () {
            return this.getImage(ImageFormat.WEBP);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PromotionComponent.prototype, "avifSrcset", {
        get: function () {
            return this.getImageSrcset(ImageFormat.AVIF);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PromotionComponent.prototype, "webpSrcset", {
        get: function () {
            return this.getImageSrcset(ImageFormat.WEBP);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PromotionComponent.prototype, "promotionGames", {
        get: function () {
            var _a, _b;
            var gameSlugs = new Set();
            (_b = (_a = this.promotion) === null || _a === void 0 ? void 0 : _a.values.games) === null || _b === void 0 ? void 0 : _b.forEach(function (game) {
                gameSlugs.add(game.slug);
            });
            return Array.from(gameSlugs.values());
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PromotionComponent.prototype, "hasPromotionGames", {
        get: function () {
            return this.promotionGames.length > 0;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PromotionComponent.prototype, "shouldShowTerms", {
        get: function () {
            return (this.showTerms || this.isUKGCJurisdiction || this.isSGAJurisdiction);
        },
        enumerable: false,
        configurable: true
    });
    PromotionComponent.prototype.getImageSrcset = function (format) {
        var _this = this;
        if (!this.promotion || !this.promotion.image) {
            return null;
        }
        return this.widths
            .map(function (width) { return _this.getImage(format, width) + " " + width + "w"; })
            .join(', ');
    };
    PromotionComponent.prototype.getImage = function (format, width) {
        if (width === void 0) { width = 1.0; }
        if (!this.promotion || !this.promotion.image) {
            return null;
        }
        return cdnService.getPromotionImageUrl(this.promotion.image, {
            width: width,
            fit: ImageFit.MAX,
            format: format,
        });
    };
    PromotionComponent.prototype.onCtaClick = function (cta, event) {
        var _a;
        if (!this.isOptIn(cta) || this.isOptedIn) {
            return;
        }
        event.preventDefault();
        this.optIn((_a = this.promotion) === null || _a === void 0 ? void 0 : _a.name, cta);
    };
    PromotionComponent.prototype.isLink = function (cta) {
        return cta.style === CtaStyle.LINK;
    };
    PromotionComponent.prototype.onPromotionsLoad = function () {
        if (!this.isLoading && this.promotions.length > 0 && !this.promotion) {
            this.handleNotFound();
            return;
        }
        this.openTerms();
    };
    /**
     * Emitted when no promotion was found for given slug and type
     */
    PromotionComponent.prototype.handleNotFound = function () {
        return;
    };
    PromotionComponent.prototype.openTerms = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.$nextTick()];
                    case 1:
                        _a.sent();
                        if (!this.showTerms || !this.$refs.terms) {
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, waitForImage(this.$refs.header)];
                    case 2:
                        _a.sent();
                        scroll.scrollTo(this.$refs.terms);
                        return [2 /*return*/];
                }
            });
        });
    };
    __decorate([
        Prop(String),
        __metadata("design:type", String)
    ], PromotionComponent.prototype, "slug", void 0);
    __decorate([
        Prop({ type: Boolean, default: false }),
        __metadata("design:type", Boolean)
    ], PromotionComponent.prototype, "showTerms", void 0);
    __decorate([
        Prop({ type: Boolean, default: false }),
        __metadata("design:type", Boolean)
    ], PromotionComponent.prototype, "isMobile", void 0);
    __decorate([
        Prop({ type: Array, default: function () { return []; } }),
        __metadata("design:type", Array)
    ], PromotionComponent.prototype, "widths", void 0);
    __decorate([
        FeatureEnabledV2({ slug: Feature.PROMOTION_GAMES }),
        __metadata("design:type", Boolean)
    ], PromotionComponent.prototype, "promotionGamesEnabled", void 0);
    __decorate([
        Watch('isLoading', { immediate: true }),
        Watch('promotions'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], PromotionComponent.prototype, "onPromotionsLoad", null);
    __decorate([
        Emit('notFound'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], PromotionComponent.prototype, "handleNotFound", null);
    PromotionComponent = __decorate([
        Component({
            name: 'promotion',
        })
    ], PromotionComponent);
    return PromotionComponent;
}(Mixins(PromotionsMixin, PromotionsOptinMixin, SessionMixin, CompetitionsMixin)));

export default PromotionComponent;
